import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, LinearProgress, TextField } from "@material-ui/core";
import { Storage } from "aws-amplify";
import { v4 as uuid } from "uuid";
import { toast } from "react-toastify";

import { createAvatar } from "../../Redux/Actions/agent";
import { localStoragePut } from "../../util/localStorage";

const isLocalAuth = process.env.REACT_APP_LOCAL_AUTH === "true";
const MAX_BYTES = 750 * 1024 * 1024;
const EXTENSIONS: Record<string, string> = { mp4: "video/mp4", webm: "video/webm" };

export const CONSENT_TEXT =
  "I am the person in this video and I consent to Viideon creating an AI avatar of me";

// Returns an error message, or null when the file is acceptable.
export const validateTrainingVideo = (file: { name: string; type: string; size: number }) => {
  const extension = (file.name.split(".").pop() || "").toLowerCase();
  const typeOk = file.type === "video/mp4" || file.type === "video/webm";
  if (!typeOk && !EXTENSIONS[extension]) return "Please choose an MP4 or WebM video.";
  if (file.size > MAX_BYTES) return "The video is larger than 750 MB. Please trim or compress it.";
  return null;
};

// "Create my digital twin": upload a training video to S3, then ask the
// backend to train a personal replica from it.
const DigitalTwinForm: React.FC = () => {
  const dispatch = useDispatch();
  const creating: boolean = useSelector((state: any) => state.agent.creatingAvatar);
  const avatarCount: number = useSelector((state: any) => state.agent.avatars.length);

  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [consent, setConsent] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [uploaded, setUploaded] = useState<{ file: File; key: string } | null>(null);
  const submittedAt = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset the form once the avatar shows up in the list.
  useEffect(() => {
    if (submittedAt.current !== null && !creating) {
      if (avatarCount > submittedAt.current) {
        setName("");
        setFile(null);
        setConsent(false);
        setUploaded(null);
        if (inputRef.current) inputRef.current.value = "";
      }
      submittedAt.current = null;
    }
  }, [creating, avatarCount]);

  const chooseFile = (chosen: File | null) => {
    if (!chosen) return;
    const error = validateTrainingVideo(chosen);
    if (error) {
      toast.error(error);
      if (inputRef.current) inputRef.current.value = "";
      setFile(null);
      return;
    }
    setFile(chosen);
  };

  const upload = async (video: File) => {
    if (uploaded && uploaded.file === video) return uploaded.key;
    const extension = (video.name.split(".").pop() || "mp4").toLowerCase();
    const contentType = video.type || EXTENSIONS[extension] || "video/mp4";
    const key = `avatar-training/${uuid()}.${EXTENSIONS[extension] ? extension : "mp4"}`;
    const progressCallback = (event: { loaded: number; total: number }) => {
      if (event.total) setProgress(Math.round((event.loaded / event.total) * 100));
    };
    setProgress(0);
    const result: any = isLocalAuth
      ? await localStoragePut(key, video, { level: "public", contentType, progressCallback })
      : await Storage.put(key, video, { level: "public", contentType, progressCallback });
    const s3Key = (result && result.key) || key;
    setUploaded({ file: video, key: s3Key });
    return s3Key;
  };

  const submit = async () => {
    if (!file || !name.trim() || !consent) return;
    try {
      const s3Key = await upload(file);
      setProgress(null);
      submittedAt.current = avatarCount;
      dispatch(createAvatar({ source: "video", name: name.trim(), s3Key, consent: true }));
    } catch (error: any) {
      setProgress(null);
      toast.error(`Upload failed: ${error?.message || "unknown error"}`);
    }
  };

  const uploading = progress !== null;
  const busy = uploading || creating;

  return (
    <div className="aiCard">
      <div className="aiCardTitle">Create my digital twin</div>
      <div className="aiCardHint">
        Record a short training video and we train an avatar that looks and sounds like you.
        Training takes from a few minutes to a few hours.
      </div>
      <div className="aiNotice">
        <strong>Recording requirements</strong>
        <ul className="avatarRequirements">
          <li>One continuous take, only you on camera</li>
          <li>About 30 seconds talking to the camera, then about 30 seconds silent with lips closed</li>
          <li>Framed from the chest up, in good, even light</li>
          <li>1080p or higher, MP4 or WebM, up to 750 MB</li>
        </ul>
      </div>

      <TextField
        label="Avatar name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        variant="outlined"
        size="small"
        fullWidth
        className="aiField"
        disabled={busy}
      />
      <div className="aiActions">
        <input
          ref={inputRef}
          id="avatar-training-video"
          type="file"
          accept="video/mp4,video/webm,.mp4,.webm"
          style={{ display: "none" }}
          onChange={(event) => chooseFile(event.target.files && event.target.files[0])}
        />
        <label htmlFor="avatar-training-video">
          <Button variant="outlined" component="span" disabled={busy}>
            {file ? "Choose a different video" : "Choose training video"}
          </Button>
        </label>
        {file && (
          <span className="aiMuted">
            {file.name} ({Math.round(file.size / (1024 * 1024))} MB)
          </span>
        )}
      </div>

      <label className="aiCheckRow">
        <input
          type="checkbox"
          checked={consent}
          onChange={(event) => setConsent(event.target.checked)}
          disabled={busy}
        />
        <span>{CONSENT_TEXT}</span>
      </label>

      {uploading && (
        <div style={{ marginTop: 12 }}>
          <LinearProgress variant="determinate" value={progress || 0} />
          <div className="aiMuted">Uploading... {progress}%</div>
        </div>
      )}

      <div className="aiActions" style={{ marginTop: 14 }}>
        <Button
          variant="contained"
          className="aiPrimaryButton"
          onClick={submit}
          disabled={busy || !file || !name.trim() || !consent}
        >
          {uploading ? "Uploading..." : creating ? "Creating..." : "Upload and create twin"}
        </Button>
      </div>
    </div>
  );
};

export default DigitalTwinForm;
